import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Option } from '../../../interfaces/option.interface';
import { OptionGroup } from '../../../interfaces/option-group.interface';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Injectable({
  providedIn: 'root',
})
export class AccessibilityService {
  private focusElementId = new Subject<string>();
  focusElementId$ = this.focusElementId.asObservable();

  constructor(private liveAnnouncer: LiveAnnouncer) {}

  /**
   * Announce message to screen readers
   * @param message The message to announce
   * @param politeness The politeness level (polite or assertive)
   */
  announce(message: string, politeness: 'polite' | 'assertive' = 'polite'): void {
    this.liveAnnouncer.announce(message, politeness);
  }

  /**
   * Announce option selection to screen readers
   * @param option The selected option
   * @param isSelected Whether the option was selected or deselected
   * @param optionGroup The option group
   */
  announceOptionSelection(option: Option, isSelected: boolean, optionGroup: OptionGroup): void {
    const action = isSelected ? 'selected' : 'removed';

    // Check if this is a half-and-half option (e.g., pizza side)
    const isHalfAndHalf = optionGroup.displayType === 'half-and-half';
    const halfMessage = this.getHalfAndHalfMessage(option);

    // Check if option is part of a required group
    const requiredMessage =
      optionGroup.minRequired > 0
        ? `. ${optionGroup.minRequired - this.getSelectedCount(optionGroup)} more selection${optionGroup.minRequired - this.getSelectedCount(optionGroup) !== 1 ? 's' : ''} required in ${optionGroup.name}`
        : '';

    const message = `${option.name} ${action}${halfMessage}${requiredMessage}`;
    this.announce(message, 'assertive');
  }

  /**
   * Get message for half-and-half options
   */
  private getHalfAndHalfMessage(option: Option): string {
    if (option.name.includes('Left Half')) {
      return ' for left side';
    } else if (option.name.includes('Right Half')) {
      return ' for right side';
    } else if (option.name.includes('Whole')) {
      return ' for whole item';
    }
    return '';
  }

  /**
   * Get count of selected options in a group
   */
  private getSelectedCount(optionGroup: OptionGroup): number {
    return optionGroup.options.filter(op => op.isSelected).length;
  }

  /**
   * Announce option quantity change
   */
  announceQuantityChange(option: Option, quantity: number, previousQuantity: number): void {
    const message = `${option.name} quantity changed from ${previousQuantity} to ${quantity}`;
    this.announce(message, 'assertive');
  }

  /**
   * Announce item added to cart
   */
  announceAddToCart(productName: string, quantity: number): void {
    this.announce(`${quantity} ${productName} added to your order`, 'assertive');
  }

  /**
   * Announce item removed from cart
   */
  announceRemoveFromCart(productName: string): void {
    this.announce(`${productName} removed from your order`, 'assertive');
  }

  /**
   * Announce item quantity updated in cart
   */
  announceCartQuantityUpdate(productName: string, quantity: number): void {
    this.announce(`${productName} quantity changed to ${quantity} in your order`, 'assertive');
  }

  /**
   * Announce validation error
   */
  announceValidationError(message: string): void {
    this.announce(`Error: ${message}`, 'assertive');
  }

  /**
   * Announce missing required selections
   */
  announceMissingRequiredSelections(optionGroups: OptionGroup[]): void {
    const invalidGroups = optionGroups.filter(group => !this.isOptionGroupValid(group));

    if (invalidGroups.length === 0) return;

    const message = `Please make required selections in: ${invalidGroups.map(g => g.name).join(', ')}`;
    this.announce(message, 'assertive');
  }

  /**
   * Check if option group is valid (has required selections)
   */
  private isOptionGroupValid(group: OptionGroup): boolean {
    const selectedOptions = group.options.filter(op => op.isSelected);
    return !group.minRequired || selectedOptions.length >= group.minRequired;
  }

  /**
   * Set focus to element by ID
   */
  setFocusToElement(elementId: string): void {
    this.focusElementId.next(elementId);
  }

  /**
   * Get ARIA attributes for an option
   */
  getOptionAriaAttributes(option: Option, optionGroup: OptionGroup): { [key: string]: string } {
    const isSelected = option.isSelected;
    const isRequired = optionGroup.minRequired > 0;
    const selectedCount = this.getSelectedCount(optionGroup);
    const remainingRequired = Math.max(0, optionGroup.minRequired - selectedCount);

    const ariaSelected = isSelected ? 'true' : 'false';
    const ariaDescribedby = `option-${option.optionID}-desc`;

    let ariaLabel = option.name;
    if (option.addedCents > 0) {
      ariaLabel += ` (additional ${option.addedCents / 100} dollars)`;
    }

    if (optionGroup.displayType === 'half-and-half') {
      if (option.name.includes('Left Half')) {
        ariaLabel += ' for left side';
      } else if (option.name.includes('Right Half')) {
        ariaLabel += ' for right side';
      } else if (option.name.includes('Whole')) {
        ariaLabel += ' for whole item';
      }
    }

    return {
      role: 'option',
      'aria-selected': ariaSelected,
      'aria-describedby': ariaDescribedby,
      'aria-label': ariaLabel,
      'aria-required': isRequired ? 'true' : 'false',
    };
  }

  /**
   * Get ARIA attributes for an option group
   */
  getOptionGroupAriaAttributes(optionGroup: OptionGroup): { [key: string]: string } {
    const isRequired = optionGroup.minRequired > 0;
    const selectedCount = this.getSelectedCount(optionGroup);
    const remainingRequired = Math.max(0, optionGroup.minRequired - selectedCount);

    let ariaLabel = optionGroup.name;
    if (isRequired) {
      ariaLabel += ` (${optionGroup.minRequired} selection${optionGroup.minRequired !== 1 ? 's' : ''} required`;
      if (remainingRequired > 0) {
        ariaLabel += `, ${remainingRequired} more needed`;
      } else {
        ariaLabel += `, complete`;
      }
      ariaLabel += ')';
    } else {
      ariaLabel += ' (optional)';
    }

    return {
      role: 'listbox',
      'aria-label': ariaLabel,
      'aria-required': isRequired ? 'true' : 'false',
      'aria-multiselectable': optionGroup.maxAllowed !== 1 ? 'true' : 'false',
    };
  }
}
